"use client";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { MEMBER_SESSION_CHANGED_EVENT } from "@/lib/auth/refresh-client";
import { apiFetch } from "@/lib/api-client";
import type { PublicSalesContact } from "@/lib/marketing/schema";
const SalesContext = createContext<PublicSalesContact | null>(null);

export function SalesContactProvider({ children }: { children: ReactNode }) {
  const path = usePathname();
  const [contact, setContact] = useState<PublicSalesContact | null>(null);
  useEffect(() => {
    let active = true;
    let controller: AbortController | undefined;
    let sequence = 0;
    const refresh = () => {
      const current = ++sequence;
      controller?.abort();
      controller = new AbortController();
      const sales = new URLSearchParams(window.location.search).get("sales") ?? undefined;
      apiFetch<{ contact: PublicSalesContact | null }>("/api/site/sales", { method: "POST", body: { sales }, signal: controller.signal })
        .then((data) => { if (active && current === sequence) setContact(data.contact); })
        .catch(() => { if (active && current === sequence) setContact(null); });
    };
    const changed = () => { setContact(null); refresh(); };
    const visible = () => { if (!document.hidden) refresh(); };
    refresh();
    window.addEventListener(MEMBER_SESSION_CHANGED_EVENT, changed);
    document.addEventListener("visibilitychange", visible);
    return () => { active = false; controller?.abort(); window.removeEventListener(MEMBER_SESSION_CHANGED_EVENT, changed); document.removeEventListener("visibilitychange", visible); };
  }, [path]);
  return <SalesContext.Provider value={contact}>{children}</SalesContext.Provider>;
}
export function useSalesContact(fallback: { qrImage?: string; qrCaption?: string }) {
  const selected = useContext(SalesContext);
  const contact = selected?.qrImage ? selected : null;
  return { contact, qrImage: contact?.qrImage || fallback.qrImage || "", qrCaption: contact ? [contact.displayName, contact.wechat, contact.phone, contact.serviceHours].filter(Boolean).join("\n") : fallback.qrCaption || "" };
}
